import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  FiUsers, FiMessageSquare, FiBookmark, FiDownload, FiRefreshCw,
  FiTrash2, FiEye, FiArrowLeft, FiDatabase, FiActivity, FiStar,
  FiBarChart2, FiTrendingUp, FiX, FiMail, FiThumbsUp, FiClock
} from 'react-icons/fi';
import { ResponsiveContainer, LineChart, Line, XAxis, YAxis, Tooltip, Legend, CartesianGrid } from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const NEED_COLORS = ['#1e3a5f', '#c9a962', '#22c55e', '#8b5cf6', '#0ea5e9'];

const STAGE_LABELS = {
  left_at_privacy: 'Left at privacy notice',
  never_typed: 'Opened chat, never typed',
  typed_never_sent: 'Typed, never sent',
  sent_messages: 'Sent messages',
};

const fmtDuration = (secs) => {
  const s = Math.max(0, Math.round(secs || 0));
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ${s % 60}s`;
  return `${Math.floor(m / 60)}h ${m % 60}m`;
};

const TABS = [
  { id: 'overview', label: 'Overview', icon: FiActivity },
  { id: 'users', label: 'Users', icon: FiUsers },
  { id: 'chats', label: 'Chats', icon: FiMessageSquare },
  { id: 'saved', label: 'Saved Recs', icon: FiBookmark },
  { id: 'ratings', label: 'Ratings', icon: FiStar },
  { id: 'feedback', label: 'Experience Feedback', icon: FiThumbsUp },
  { id: 'duration', label: 'Duration', icon: FiClock },
  { id: 'messages', label: 'Messages', icon: FiMail },
  { id: 'analytics', label: 'Analytics', icon: FiBarChart2 },
];

export default function AdminPage() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [chats, setChats] = useState([]);
  const [saved, setSaved] = useState([]);
  const [ratings, setRatings] = useState(null);
  const [expFeedback, setExpFeedback] = useState(null);
  const [visits, setVisits] = useState(null);
  const [visitFilter, setVisitFilter] = useState('all');
  const [analytics, setAnalytics] = useState(null);
  const [trends, setTrends] = useState(null);
  const [messages, setMessages] = useState([]);
  const [transcript, setTranscript] = useState(null);
  const [transcriptLoading, setTranscriptLoading] = useState(false);

  useEffect(() => {
    const checkAccess = async () => {
      try {
        const res = await fetch(`${API_URL}/api/auth/me`, { credentials: 'include' });
        if (!res.ok) { navigate('/login'); return; }
        const me = await res.json();
        if (!me.is_admin) { navigate('/dashboard'); return; }
        setAuthorized(true);
      } catch (error) {
        navigate('/login');
      }
    };
    checkAccess();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (authorized) fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, authorized]);

  const fetchData = async () => {
    setLoading(true);
    try {
      if (activeTab === 'overview' || !stats) {
        const r = await fetch(`${API_URL}/api/admin/stats`, { credentials: 'include' });
        if (r.ok) setStats(await r.json());
      }
      if (activeTab === 'users') {
        const r = await fetch(`${API_URL}/api/admin/users`, { credentials: 'include' });
        if (r.ok) setUsers((await r.json()).users);
      }
      if (activeTab === 'chats') {
        const r = await fetch(`${API_URL}/api/admin/chats`, { credentials: 'include' });
        if (r.ok) setChats((await r.json()).sessions);
      }
      if (activeTab === 'saved') {
        const r = await fetch(`${API_URL}/api/admin/saved-recommendations`, { credentials: 'include' });
        if (r.ok) setSaved((await r.json()).saved);
      }
      if (activeTab === 'ratings') {
        const r = await fetch(`${API_URL}/api/admin/ratings`, { credentials: 'include' });
        if (r.ok) setRatings(await r.json());
      }
      if (activeTab === 'ratings' || activeTab === 'feedback') {
        const r = await fetch(`${API_URL}/api/admin/experience-feedback`, { credentials: 'include' });
        if (r.ok) setExpFeedback(await r.json());
      }
      if (activeTab === 'duration') {
        const r = await fetch(`${API_URL}/api/admin/visits`, { credentials: 'include' });
        if (r.ok) setVisits(await r.json());
      }
      if (activeTab === 'messages') {
        const r = await fetch(`${API_URL}/api/admin/messages`, { credentials: 'include' });
        if (r.ok) setMessages((await r.json()).messages);
      }
      if (activeTab === 'analytics') {
        const r = await fetch(`${API_URL}/api/admin/analytics`, { credentials: 'include' });
        if (r.ok) setAnalytics(await r.json());
        const t = await fetch(`${API_URL}/api/admin/analytics/trends`, { credentials: 'include' });
        if (t.ok) setTrends(await t.json());
      }
    } catch (error) {
      console.error('Failed to fetch admin data:', error);
    }
    setLoading(false);
  };

  const exportData = async (type) => {
    try {
      const res = await fetch(`${API_URL}/api/admin/export/${type}`, { credentials: 'include' });
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.json`;
      a.click();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const deleteUser = async (userId) => {
    if (!window.confirm('Delete this user? This cannot be undone.')) return;
    try {
      await fetch(`${API_URL}/api/admin/users/${userId}`, { method: 'DELETE', credentials: 'include' });
      setUsers(users.filter(u => u.user_id !== userId));
    } catch (error) {
      console.error('Delete failed:', error);
    }
  };

  const viewTranscript = async (session) => {
    setTranscript({ session, messages: [] });
    setTranscriptLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/chats/${session.session_id}`, { credentials: 'include' });
      if (r.ok) {
        const data = await r.json();
        setTranscript({ session, messages: data.messages || [] });
      }
    } catch (error) {
      console.error('Transcript failed:', error);
    }
    setTranscriptLoading(false);
  };

  const StatCard = ({ icon: Icon, label, value, color }) => (
    <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100">
      <div className="flex items-center gap-4">
        <div className={`w-12 h-12 ${color} rounded-lg flex items-center justify-center`}>
          <Icon className="w-6 h-6 text-white" />
        </div>
        <div>
          <p className="text-2xl font-bold text-[#1e3a5f]">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );

  const BarList = ({ title, items, icon: Icon, colorClass = 'bg-[#1e3a5f]' }) => {
    const max = items && items.length ? Math.max(...items.map(i => i.count)) : 1;
    return (
      <div className="bg-white rounded-xl p-5 shadow-sm">
        <h3 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2">
          {Icon && <Icon className="w-4 h-4" />} {title}
        </h3>
        {(!items || items.length === 0) ? (
          <p className="text-sm text-gray-400">No data yet.</p>
        ) : (
          <div className="space-y-2.5">
            {items.map((it, idx) => (
              <div key={idx} data-testid={`bar-${title}-${idx}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="text-[#1e3a5f] capitalize truncate pr-2">{it.label}</span>
                  <span className="text-gray-500 shrink-0">{it.count}</span>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden">
                  <div className={`h-full ${colorClass} rounded-full`} style={{ width: `${Math.max(6, (it.count / max) * 100)}%` }} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-[#f5f0e8]">
      {!authorized ? (
        <div className="flex items-center justify-center min-h-screen" data-testid="admin-access-check">
          <FiRefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
        </div>
      ) : (
      <>
      <header className="bg-[#1e3a5f] py-4 px-6">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <button onClick={() => navigate('/dashboard')} className="text-[#f5f0e6] hover:bg-[#2a4d6e] p-2 rounded-lg" data-testid="admin-back-btn">
              <FiArrowLeft className="w-5 h-5" />
            </button>
            <span className="font-bold text-xl text-[#f5f0e6]">Admin Dashboard</span>
          </div>
          <button onClick={fetchData} className="flex items-center gap-2 text-[#f5f0e6] hover:bg-[#2a4d6e] px-3 py-2 rounded-lg" data-testid="admin-refresh-btn">
            <FiRefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto p-6">
        {/* Tabs */}
        <div className="flex gap-2 mb-6 bg-white rounded-xl p-2 shadow-sm overflow-x-auto">
          {TABS.map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors whitespace-nowrap ${
                activeTab === tab.id ? 'bg-[#1e3a5f] text-white' : 'text-[#1e3a5f] hover:bg-gray-100'
              }`}
              data-testid={`admin-tab-${tab.id}`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <FiRefreshCw className="w-8 h-8 animate-spin text-[#1e3a5f]" />
          </div>
        ) : (
          <>
            {/* Overview */}
            {activeTab === 'overview' && stats && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                  <StatCard icon={FiUsers} label="Total Users" value={stats.total_users} color="bg-blue-500" />
                  <StatCard icon={FiMessageSquare} label="Total Messages" value={stats.total_messages} color="bg-green-500" />
                  <StatCard icon={FiBookmark} label="Saved Recs" value={stats.total_saved} color="bg-yellow-500" />
                  <StatCard icon={FiDatabase} label="Chat Sessions" value={stats.total_sessions} color="bg-purple-500" />
                  <StatCard icon={FiStar} label={`Avg Rating (${stats.total_ratings})`} value={stats.avg_rating || '—'} color="bg-[#c9a962]" />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2"><FiUsers className="w-5 h-5" /> Recent Users</h3>
                    <div className="space-y-3">
                      {stats.recent_users?.map(user => (
                        <div key={user.user_id} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                          <div>
                            <p className="font-medium text-[#1e3a5f]">{user.name || 'No name'}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </div>
                          <span className="text-xs text-gray-400">{user.created_at ? new Date(user.created_at).toLocaleDateString() : ''}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="bg-white rounded-xl p-6 shadow-sm">
                    <h3 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2"><FiDownload className="w-5 h-5" /> Export Data</h3>
                    <div className="space-y-3">
                      {[
                        { t: 'users', label: 'Export Users', icon: FiUsers, c: 'text-blue-500' },
                        { t: 'chats', label: 'Export Chat History', icon: FiMessageSquare, c: 'text-green-500' },
                        { t: 'saved-recommendations', label: 'Export Saved Recs', icon: FiBookmark, c: 'text-yellow-500' },
                        { t: 'ratings', label: 'Export Ratings', icon: FiStar, c: 'text-[#c9a962]' },
                        { t: 'experience-feedback', label: 'Export Experience Feedback', icon: FiThumbsUp, c: 'text-green-500' },
                        { t: 'visits', label: 'Export Visit Durations', icon: FiClock, c: 'text-purple-500' },
                        { t: 'analytics', label: 'Export Analytics', icon: FiBarChart2, c: 'text-purple-500' },
                      ].map(x => (
                        <button key={x.t} onClick={() => exportData(x.t)} className="w-full flex items-center justify-between p-3 bg-gray-50 hover:bg-gray-100 rounded-lg transition-colors" data-testid={`export-${x.t}`}>
                          <span className="flex items-center gap-2"><x.icon className={`w-4 h-4 ${x.c}`} /> {x.label}</span>
                          <FiDownload className="w-4 h-4 text-gray-400" />
                        </button>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Users */}
            {activeTab === 'users' && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-[#1e3a5f]">All Users ({users.length})</h3>
                  <button onClick={() => exportData('users')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg"><FiDownload className="w-4 h-4" /> Export</button>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">User</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Role / Company</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Industry</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Joined</th>
                        <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y">
                      {users.map(user => (
                        <tr key={user.user_id} className="hover:bg-gray-50" data-testid={`user-row-${user.user_id}`}>
                          <td className="px-4 py-3">
                            <p className="font-medium text-[#1e3a5f]">{user.name || 'No name'}</p>
                            <p className="text-sm text-gray-500">{user.email}</p>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-[#1e3a5f]">{user.role || '-'}</p>
                            <p className="text-xs text-gray-500">{user.company || '-'}</p>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-600">{user.industry || '-'}</td>
                          <td className="px-4 py-3 text-sm text-gray-500">{user.created_at ? new Date(user.created_at).toLocaleDateString() : '-'}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => deleteUser(user.user_id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg" title="Delete user"><FiTrash2 className="w-4 h-4" /></button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Chats */}
            {activeTab === 'chats' && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-[#1e3a5f]">Chat Sessions ({chats.length})</h3>
                  <button onClick={() => exportData('chats')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg"><FiDownload className="w-4 h-4" /> Export</button>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {chats.map((session, idx) => (
                    <div key={session.session_id || idx} className="p-4 hover:bg-gray-50 flex items-start justify-between gap-3" data-testid={`chat-row-${idx}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-1">
                          <span className="text-sm font-semibold text-[#1e3a5f]">{session.user_email || 'Guest'}</span>
                          <span className="text-xs text-gray-400">· {session.message_count} messages</span>
                        </div>
                        {session.title && <p className="text-sm text-[#1e3a5f]/80 font-medium">{session.title}</p>}
                        {session.preview && <p className="text-sm text-gray-500 line-clamp-2">{session.preview}</p>}
                        <p className="text-xs text-gray-400 mt-1">{session.last_activity ? new Date(session.last_activity).toLocaleString() : ''}</p>
                      </div>
                      <button onClick={() => viewTranscript(session)} className="flex items-center gap-1.5 text-sm text-[#1e3a5f] border border-gray-200 hover:bg-gray-100 px-3 py-1.5 rounded-lg shrink-0" data-testid={`view-transcript-${idx}`}>
                        <FiEye className="w-4 h-4" /> View
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Saved Recommendations */}
            {activeTab === 'saved' && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-[#1e3a5f]">Saved Recommendations ({saved.length})</h3>
                  <button onClick={() => exportData('saved-recommendations')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg"><FiDownload className="w-4 h-4" /> Export</button>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {saved.length === 0 ? (
                    <p className="p-6 text-sm text-gray-400">No saved recommendations yet.</p>
                  ) : saved.map((s, idx) => (
                    <div key={s.saved_id || idx} className="p-4 hover:bg-gray-50" data-testid={`saved-row-${idx}`}>
                      <div className="flex items-center justify-between mb-1">
                        <span className="font-semibold text-[#1e3a5f]">{s.program_name}</span>
                        <span className="text-xs text-gray-400">{s.created_at ? new Date(s.created_at).toLocaleDateString() : ''}</span>
                      </div>
                      {s.organization && <p className="text-sm text-gray-500">{s.organization}</p>}
                      {s.context_query && <p className="text-xs text-[#c9a962] mt-1 italic">Asked: "{s.context_query.substring(0, 80)}"</p>}
                      <p className="text-xs text-gray-400 mt-1">Saved by: {s.user_email}</p>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Ratings */}
            {activeTab === 'ratings' && ratings && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard icon={FiStar} label="Average Rating" value={ratings.average || '—'} color="bg-[#c9a962]" />
                  <StatCard icon={FiMessageSquare} label="Total Ratings" value={ratings.count} color="bg-blue-500" />
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-sm font-medium text-gray-500 mb-2">Distribution</p>
                    <div className="space-y-1">
                      {[5, 4, 3, 2, 1].map(star => {
                        const c = (ratings.distribution && ratings.distribution[star]) || 0;
                        const pct = ratings.count ? (c / ratings.count) * 100 : 0;
                        return (
                          <div key={star} className="flex items-center gap-2 text-xs">
                            <span className="w-6 text-[#1e3a5f]">{star}★</span>
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-[#c9a962] rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-6 text-right text-gray-500">{c}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
                {/* Sitewide experience feedback summary (full detail in the Experience Feedback tab) */}
                {expFeedback && (
                  <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="ratings-experience-summary">
                    <div className="flex items-center justify-between mb-3">
                      <h3 className="font-bold text-[#1e3a5f] flex items-center gap-2"><FiThumbsUp className="w-4 h-4" /> Overall Experience Feedback</h3>
                      <button onClick={() => setActiveTab('feedback')} className="text-sm text-[#1e3a5f] underline hover:text-[#2a4d6e]" data-testid="ratings-view-experience-btn">View all</button>
                    </div>
                    <div className="flex flex-wrap items-center gap-6">
                      <div>
                        <p className="text-2xl font-bold text-[#1e3a5f]">{expFeedback.average || '—'}</p>
                        <p className="text-xs text-gray-500">avg stars ({expFeedback.count} responses)</p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {(expFeedback.tags || []).slice(0, 6).map(t => (
                          <span key={t.label} className="text-xs bg-[#f5f0e6] text-[#1e3a5f] border border-[#e8e0d0] px-2 py-0.5 rounded-full">{t.label} · {t.count}</span>
                        ))}
                        {(!expFeedback.tags || expFeedback.tags.length === 0) && <span className="text-sm text-gray-400">No experience feedback yet.</span>}
                      </div>
                    </div>
                  </div>
                )}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 border-b flex items-center justify-between">
                    <h3 className="font-bold text-[#1e3a5f]">Feedback ({ratings.ratings.length})</h3>                    <button onClick={() => exportData('ratings')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg"><FiDownload className="w-4 h-4" /> Export</button>
                  </div>
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {ratings.ratings.length === 0 ? (
                      <p className="p-6 text-sm text-gray-400">No ratings yet. They'll appear here once users rate their chats.</p>
                    ) : ratings.ratings.map((r, idx) => (
                      <div key={r.rating_id || idx} className="p-4 hover:bg-gray-50" data-testid={`rating-row-${idx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[#c9a962]">{'★'.repeat(r.stars)}<span className="text-gray-300">{'★'.repeat(5 - r.stars)}</span></span>
                            <span className="text-sm text-gray-600">{r.user_email || 'Guest'}</span>
                          </div>
                          <span className="text-xs text-gray-400">{r.created_at ? new Date(r.created_at).toLocaleDateString() : ''}</span>
                        </div>
                        {r.comment ? <p className="text-sm text-gray-700">{r.comment}</p> : <p className="text-sm text-gray-400 italic">No comment</p>}
                        {r.tags && r.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {r.tags.map((t, ti) => (
                              <span key={ti} className="text-xs bg-[#f5f0e6] text-[#1e3a5f] border border-[#e8e0d0] px-2 py-0.5 rounded-full">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Experience Feedback (sitewide floating widget) */}
            {activeTab === 'feedback' && expFeedback && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <StatCard icon={FiStar} label="Average Experience Rating" value={expFeedback.average || '—'} color="bg-[#c9a962]" />
                  <StatCard icon={FiThumbsUp} label="Total Responses" value={expFeedback.count} color="bg-green-500" />
                  <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                    <p className="text-sm font-medium text-gray-500 mb-2">Distribution</p>
                    <div className="space-y-1">
                      {[5, 4, 3, 2, 1].map(star => {
                        const c = (expFeedback.distribution && expFeedback.distribution[star]) || 0;
                        const pct = expFeedback.count ? (c / expFeedback.count) * 100 : 0;
                        return (
                          <div key={star} className="flex items-center gap-2 text-xs">
                            <span className="w-6 text-[#1e3a5f]">{star}★</span>
                            <div className="flex-1 h-2 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-[#c9a962] rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                            <span className="w-6 text-right text-gray-500">{c}</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                <BarList title="Experience Tags" items={expFeedback.tags} icon={FiThumbsUp} colorClass="bg-[#1e3a5f]" />

                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 border-b flex items-center justify-between">
                    <h3 className="font-bold text-[#1e3a5f]">Responses ({expFeedback.feedback.length})</h3>
                    <button onClick={() => exportData('experience-feedback')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg" data-testid="export-experience-feedback"><FiDownload className="w-4 h-4" /> Export</button>
                  </div>
                  <div className="divide-y max-h-[500px] overflow-y-auto">
                    {expFeedback.feedback.length === 0 ? (
                      <p className="p-6 text-sm text-gray-400">No experience feedback yet. It appears here when users use the floating feedback widget.</p>
                    ) : expFeedback.feedback.map((f, idx) => (
                      <div key={f.feedback_id || idx} className="p-4 hover:bg-gray-50" data-testid={`experience-feedback-row-${idx}`}>
                        <div className="flex items-center justify-between mb-1">
                          <div className="flex items-center gap-2">
                            <span className="text-[#c9a962]">{'★'.repeat(f.stars)}<span className="text-gray-300">{'★'.repeat(5 - f.stars)}</span></span>
                            <span className="text-sm text-gray-600">{f.user_email || 'Guest'}</span>
                          </div>
                          <span className="text-xs text-gray-400">{f.created_at ? new Date(f.created_at).toLocaleString() : ''}</span>
                        </div>
                        {f.comment ? <p className="text-sm text-gray-700">{f.comment}</p> : <p className="text-sm text-gray-400 italic">No comment</p>}
                        {f.tags && f.tags.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {f.tags.map((t, ti) => (
                              <span key={ti} className="text-xs bg-[#f5f0e6] text-[#1e3a5f] border border-[#e8e0d0] px-2 py-0.5 rounded-full">{t}</span>
                            ))}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* Duration — one row per visit */}
            {activeTab === 'duration' && visits && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <StatCard icon={FiUsers} label="Total Visits" value={visits.count} color="bg-blue-500" />
                  <StatCard icon={FiClock} label="Avg Time on Site" value={fmtDuration(visits.avg_duration_seconds)} color="bg-purple-500" />
                  <StatCard icon={FiMessageSquare} label={`Avg Chat Length (${visits.with_chat} chats)`} value={fmtDuration(visits.avg_chat_duration_seconds)} color="bg-green-500" />
                  <StatCard icon={FiThumbsUp} label="Visits With Feedback" value={visits.with_feedback} color="bg-[#c9a962]" />
                </div>
                {/* Drop-off funnel */}
                {visits.funnel && (
                  <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="dropoff-panel">
                    <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
                      <h3 className="font-bold text-[#1e3a5f] flex items-center gap-2"><FiTrendingUp className="w-4 h-4" /> Drop-off Funnel</h3>
                      <span className="text-sm text-gray-500">Bounce rate (left without sending a message): <span className="font-bold text-[#1e3a5f]">{visits.funnel.bounce_rate}%</span></span>
                    </div>
                    <div className="space-y-2.5">
                      {[
                        { key: 'saw_privacy_notice', label: 'Saw privacy notice' },
                        { key: 'accepted_privacy', label: 'Accepted it' },
                        { key: 'typed_in_box', label: 'Typed in the chat box' },
                        { key: 'sent_messages', label: 'Sent a message' },
                      ].map(step => {
                        const c = visits.funnel[step.key] || 0;
                        const pct = visits.funnel.saw_privacy_notice ? (c / visits.funnel.saw_privacy_notice) * 100 : 0;
                        return (
                          <div key={step.key} data-testid={`funnel-step-${step.key}`}>
                            <div className="flex items-center justify-between text-sm mb-1">
                              <span className="text-[#1e3a5f]">{step.label}</span>
                              <span className="text-gray-500">{c} · {Math.round(pct)}%</span>
                            </div>
                            <div className="h-2.5 bg-gray-100 rounded-full overflow-hidden">
                              <div className="h-full bg-[#1e3a5f] rounded-full" style={{ width: `${Math.max(2, pct)}%` }} />
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mt-5">
                      {[
                        { key: 'left_at_privacy', label: 'Left at privacy notice' },
                        { key: 'never_typed', label: 'Opened chat, never typed' },
                        { key: 'typed_never_sent', label: 'Typed, never sent' },
                      ].map(x => (
                        <div key={x.key} className="bg-[#f5f0e8] rounded-lg p-3" data-testid={`dropoff-${x.key}`}>
                          <p className="text-xl font-bold text-[#1e3a5f]">{visits.funnel[x.key] || 0}</p>
                          <p className="text-xs text-gray-500">{x.label}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                  <div className="p-4 border-b flex items-center justify-between gap-3 flex-wrap">
                    <h3 className="font-bold text-[#1e3a5f]">Visits ({visits.visits.filter(v => visitFilter === 'all' || !v.message_count).length})</h3>
                    <div className="flex items-center gap-2">
                      <div className="flex rounded-lg border border-gray-200 overflow-hidden text-sm">
                        <button onClick={() => setVisitFilter('all')} className={`px-3 py-1.5 ${visitFilter === 'all' ? 'bg-[#1e3a5f] text-white' : 'text-[#1e3a5f] hover:bg-gray-100'}`} data-testid="visit-filter-all">All visits</button>
                        <button onClick={() => setVisitFilter('dropped')} className={`px-3 py-1.5 ${visitFilter === 'dropped' ? 'bg-[#1e3a5f] text-white' : 'text-[#1e3a5f] hover:bg-gray-100'}`} data-testid="visit-filter-dropped">Left without messaging</button>
                      </div>
                      <button onClick={() => exportData('visits')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg" data-testid="export-visits"><FiDownload className="w-4 h-4" /> Export</button>
                    </div>
                  </div>
                  <div className="overflow-x-auto max-h-[600px]">
                    <table className="w-full">
                      <thead className="bg-gray-50 sticky top-0">
                        <tr>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Visitor</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Started</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Time on Site</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Chat Duration</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Messages</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Got to</th>
                          <th className="text-left px-4 py-3 text-sm font-medium text-gray-600">Feedback</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y">
                        {(() => {
                          const rows = visits.visits.filter(v => visitFilter === 'all' || !v.message_count);
                          if (rows.length === 0) {
                            return <tr><td colSpan={7} className="p-6 text-sm text-gray-400">No visits to show.</td></tr>;
                          }
                          return rows.map((v, idx) => (
                            <tr key={v.visit_id || idx} className="hover:bg-gray-50" data-testid={`visit-row-${idx}`}>
                              <td className="px-4 py-3 text-sm text-[#1e3a5f]">{v.user_email && v.user_email !== 'Guest' ? v.user_email : 'Guest (anonymous)'}</td>
                              <td className="px-4 py-3 text-sm text-gray-500">{v.started_at ? new Date(v.started_at).toLocaleString() : '-'}</td>
                              <td className="px-4 py-3 text-sm text-[#1e3a5f] font-medium">{fmtDuration(v.duration_seconds)}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{v.message_count ? fmtDuration(v.chat_duration_seconds) : '—'}</td>
                              <td className="px-4 py-3 text-sm text-gray-600">{v.message_count || 0}</td>
                              <td className="px-4 py-3 text-sm">
                                <span className={`text-xs px-2 py-0.5 rounded-full ${v.stage === 'sent_messages' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                  {STAGE_LABELS[v.stage] || v.stage}
                                </span>
                              </td>
                              <td className="px-4 py-3 text-sm">
                                {v.feedback_given
                                  ? <span className="text-green-600 font-medium">✓ {v.feedback_stars ? `${v.feedback_stars}★` : 'Yes'}</span>
                                  : <span className="text-gray-400">✗ No</span>}
                              </td>
                            </tr>
                          ));
                        })()}
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            )}

            {/* Messages (Contact + Concierge) */}
            {activeTab === 'messages' && (
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="p-4 border-b flex items-center justify-between">
                  <h3 className="font-bold text-[#1e3a5f]">Contact & Concierge ({messages.length})</h3>
                </div>
                <div className="divide-y max-h-[600px] overflow-y-auto">
                  {messages.length === 0 ? (
                    <p className="p-6 text-sm text-gray-400">No messages yet. Contact and concierge submissions will appear here.</p>
                  ) : messages.map((m, idx) => (
                    <div key={m.message_id || idx} className="p-4 hover:bg-gray-50" data-testid={`message-row-${idx}`}>
                      <div className="flex items-center justify-between mb-1 gap-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${m.type === 'concierge' ? 'bg-[#c9a962]/20 text-[#8a6d1f]' : 'bg-blue-100 text-blue-700'}`}>
                            {m.type === 'concierge' ? 'Concierge' : 'Contact'}
                          </span>
                          <span className="text-sm font-semibold text-[#1e3a5f]">{m.subject || '(no subject)'}</span>
                          {!m.emailed && <span className="text-xs text-amber-600" title="Email not sent (Resend key not configured)">✉︎ not emailed</span>}
                        </div>
                        <span className="text-xs text-gray-400 shrink-0">{m.created_at ? new Date(m.created_at).toLocaleString() : ''}</span>
                      </div>
                      <p className="text-xs text-gray-500 mb-1">{m.name ? `${m.name} · ` : ''}{m.email}</p>
                      <p className="text-sm text-gray-700 whitespace-pre-wrap">{m.message}</p>
                      {m.profile_snapshot && Object.values(m.profile_snapshot).some(Boolean) && (
                        <div className="flex flex-wrap gap-1.5 mt-2">
                          {Object.entries(m.profile_snapshot).filter(([, v]) => v).map(([k, v]) => (
                            <span key={k} className="text-xs bg-[#f5f0e6] text-[#1e3a5f] border border-[#e8e0d0] px-2 py-0.5 rounded-full">{k}: {v}</span>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Analytics */}
            {activeTab === 'analytics' && analytics && (
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <p className="text-sm text-gray-500">Based on {analytics.total_analyzed} analyzed messages</p>
                  <button onClick={() => exportData('analytics')} className="flex items-center gap-2 text-sm text-[#1e3a5f] hover:bg-gray-100 px-3 py-1.5 rounded-lg bg-white shadow-sm"><FiDownload className="w-4 h-4" /> Export</button>
                </div>

                {/* Weekly trends */}
                {trends && trends.series && trends.series.length > 0 && (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="trend-sentiment">
                      <h3 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2"><FiTrendingUp className="w-4 h-4" /> Sentiment Over Time (weekly)</h3>
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={trends.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          <Line type="monotone" dataKey="positive" stroke="#22c55e" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="neutral" stroke="#94a3b8" strokeWidth={2} dot={false} />
                          <Line type="monotone" dataKey="negative" stroke="#ef4444" strokeWidth={2} dot={false} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="bg-white rounded-xl p-5 shadow-sm" data-testid="trend-needs">
                      <h3 className="font-bold text-[#1e3a5f] mb-4 flex items-center gap-2"><FiBarChart2 className="w-4 h-4" /> Top Needs Over Time (weekly)</h3>
                      <ResponsiveContainer width="100%" height={240}>
                        <LineChart data={trends.series} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                          <XAxis dataKey="week" tick={{ fontSize: 11 }} />
                          <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                          <Tooltip />
                          <Legend />
                          {(trends.top_needs || []).map((n, i) => (
                            <Line key={n} type="monotone" dataKey={n} stroke={NEED_COLORS[i % NEED_COLORS.length]} strokeWidth={2} dot={false} />
                          ))}
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  <BarList title="Sentiment" items={analytics.sentiment} icon={FiTrendingUp} colorClass="bg-green-500" />
                  <BarList title="Top Needs" items={analytics.needs} icon={FiBarChart2} colorClass="bg-[#1e3a5f]" />
                  <BarList title="Stages" items={analytics.stages} icon={FiActivity} colorClass="bg-purple-500" />
                  <BarList title="Industries" items={analytics.industries} icon={FiDatabase} colorClass="bg-blue-500" />
                  <BarList title="Locations" items={analytics.locations} icon={FiUsers} colorClass="bg-teal-500" />
                  <BarList title="Common Themes" items={analytics.topics} icon={FiMessageSquare} colorClass="bg-[#c9a962]" />
                </div>
                <BarList title="Top Keywords" items={analytics.keywords} icon={FiBarChart2} colorClass="bg-orange-400" />
              </div>
            )}
          </>
        )}
      </div>

      {/* Transcript Modal */}
      {transcript && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setTranscript(null)}>
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[80vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="transcript-modal">
            <div className="p-4 border-b flex items-center justify-between">
              <div className="min-w-0">
                <h3 className="font-bold text-[#1e3a5f] truncate">{transcript.session.title || 'Chat Transcript'}</h3>
                <p className="text-xs text-gray-500">{transcript.session.user_email}</p>
              </div>
              <button onClick={() => setTranscript(null)} className="text-gray-400 hover:text-gray-700 p-1 rounded-lg hover:bg-gray-100" data-testid="transcript-close-btn"><FiX className="w-5 h-5" /></button>
            </div>
            <div className="p-4 overflow-y-auto space-y-3">
              {transcriptLoading ? (
                <div className="flex justify-center py-8"><FiRefreshCw className="w-6 h-6 animate-spin text-[#1e3a5f]" /></div>
              ) : transcript.messages.map((m, i) => (
                <div key={i} className={`p-3 rounded-lg ${m.role === 'user' ? 'bg-[#e8e0d0] ml-8' : 'bg-gray-50 mr-8'}`}>
                  <p className="text-xs font-semibold text-[#1e3a5f]/60 mb-1">{m.role === 'user' ? 'User' : 'AI'}</p>
                  <p className="text-sm text-[#1e3a5f] whitespace-pre-wrap">{m.content}</p>
                  {m.recommendations && m.recommendations.length > 0 && (
                    <p className="text-xs text-[#c9a962] mt-1">+ {m.recommendations.length} program recommendation(s)</p>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      </>
      )}
    </div>
  );
}
